import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";
import { flattenAdminModules } from "../../helper/permissions";

const customModalStyles = {
  content: {
    padding: "20px",
    width: "90%",
    maxWidth: "640px",
    margin: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    maxHeight: "90vh",
    overflow: "auto",
  },
};

const emptyForm = {
  name: "",
  email: "",
  password: "",
  role: "admin",
  permissions: [],
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("users/all");
      setUsers(response?.data?.data?.users || []);
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to load users",
        "error"
      );
    }
    setLoading(false);
  };

  const openCreate = () => {
    setEditId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditId(user.id);
    setFormData({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: user.role === "limited" ? "limited" : "admin",
      permissions: user.role === "limited" ? user.permissions || [] : [],
    });
    setShowModal(true);
  };

  const isPermissionChecked = (key) => {
    if (!formData.permissions || !Array.isArray(formData.permissions)) return false;
    if (key === "home-page") {
      return (
        formData.permissions.includes("home-page") ||
        formData.permissions.includes("home-settings") ||
        formData.permissions.includes("home_page")
      );
    }
    if (key === "university-life") {
      return (
        formData.permissions.includes("university-life") ||
        formData.permissions.includes("cms-pages")
      );
    }
    return formData.permissions.includes(key);
  };

  const togglePermission = (key) => {
    setFormData((prev) => {
      const currentList = prev.permissions || [];
      const isChecked = isPermissionChecked(key);

      let nextPermissions;
      if (isChecked) {
        if (key === "home-page") {
          nextPermissions = currentList.filter(
            (p) => p !== "home-page" && p !== "home-settings" && p !== "home_page"
          );
        } else if (key === "university-life") {
          nextPermissions = currentList.filter(
            (p) => p !== "university-life" && p !== "cms-pages"
          );
        } else {
          nextPermissions = currentList.filter((p) => p !== key);
        }
      } else {
        nextPermissions = [...currentList, key];
      }

      return {
        ...prev,
        permissions: nextPermissions,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.role === "limited" && formData.permissions.length === 0) {
      Swal.fire("Error", "Select at least one module for limited access.", "error");
      return;
    }

    setButtonLoading(true);
    const payload = {
      name: formData.name,
      email: formData.email,
      role: formData.role,
      permissions: formData.role === "limited" ? formData.permissions : [],
    };
    if (formData.password) {
      payload.password = formData.password;
    }

    try {
      if (editId) {
        await axiosInstance.put(`users/change/${editId}`, payload);
        Swal.fire("Success!", "User updated successfully!", "success");
      } else {
        if (!formData.password) {
          Swal.fire("Error", "Password is required.", "error");
          setButtonLoading(false);
          return;
        }
        await axiosInstance.post("users/create", payload);
        Swal.fire("Success!", "User created successfully!", "success");
      }
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to save user",
        "error"
      );
    }
    setButtonLoading(false);
  };

  const handleDelete = async (user) => {
    if (user.role === "superadmin") return;

    const result = await Swal.fire({
      title: "Delete user?",
      text: `Remove ${user.email}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`users/delete/${user.id}`);
      Swal.fire("Deleted!", "User removed.", "success");
      fetchUsers();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete user",
        "error"
      );
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0 }}>Users</h2>
          <p style={{ margin: "6px 0 0", color: "#666" }}>
            Superadmin can create full-access admins or limited-access users.
          </p>
        </div>
        <button
          onClick={openCreate}
          style={{
            padding: "10px 16px",
            background: "#4a90e2",
            color: "#fff",
            border: 0,
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          Create User
        </button>
      </div>

      {loading ? (
        <CircularProgress />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
            <thead>
              <tr style={{ background: "#f5f7fb", textAlign: "left" }}>
                <th style={{ padding: 12 }}>Name</th>
                <th style={{ padding: 12 }}>Email</th>
                <th style={{ padding: 12 }}>Role</th>
                <th style={{ padding: 12 }}>Access</th>
                <th style={{ padding: 12 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} style={{ borderTop: "1px solid #eee" }}>
                  <td style={{ padding: 12 }}>{user.name}</td>
                  <td style={{ padding: 12 }}>{user.email}</td>
                  <td style={{ padding: 12, textTransform: "capitalize" }}>{user.role}</td>
                  <td style={{ padding: 12, fontSize: 13, color: "#555" }}>
                    {user.role === "limited"
                      ? (user.permissions || []).join(", ") || "None"
                      : "Full access"}
                  </td>
                  <td style={{ padding: 12 }}>
                    {user.role !== "superadmin" && (
                      <>
                        <button
                          onClick={() => openEdit(user)}
                          style={{ marginRight: 8, padding: "6px 10px", cursor: "pointer" }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          style={{ padding: "6px 10px", cursor: "pointer", color: "#c62828" }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        style={customModalStyles}
        contentLabel="User form"
        ariaHideApp={false}
      >
        <h3 style={{ marginTop: 0 }}>{editId ? "Edit User" : "Create User"}</h3>
        <form onSubmit={handleSubmit}>
          <label style={{ display: "block", marginBottom: 8 }}>
            Name
            <input
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{ width: "100%", padding: 8, marginTop: 4 }}
            />
          </label>
          <label style={{ display: "block", marginBottom: 8 }}>
            Email
            <input
              required
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              style={{ width: "100%", padding: 8, marginTop: 4 }}
            />
          </label>
          <label style={{ display: "block", marginBottom: 8 }}>
            Password {editId ? "(leave blank to keep)" : ""}
            <input
              type="password"
              required={!editId}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              style={{ width: "100%", padding: 8, marginTop: 4 }}
            />
          </label>
          <label style={{ display: "block", marginBottom: 12 }}>
            Access level
            <select
              value={formData.role}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  role: e.target.value,
                  permissions: e.target.value === "admin" ? [] : formData.permissions,
                })
              }
              style={{ width: "100%", padding: 8, marginTop: 4 }}
            >
              <option value="admin">Admin (full access)</option>
              <option value="limited">Limited access</option>
            </select>
          </label>

          {formData.role === "limited" && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ marginBottom: 8, fontWeight: 600 }}>Allowed modules</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {flattenAdminModules().map((mod) => (
                  <label key={mod.key} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={isPermissionChecked(mod.key)}
                      onChange={() => togglePermission(mod.key)}
                    />
                    {mod.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" onClick={() => setShowModal(false)} style={{ padding: "8px 14px" }}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={buttonLoading}
              style={{
                padding: "8px 14px",
                background: "#4a90e2",
                color: "#fff",
                border: 0,
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              {buttonLoading ? "Saving..." : editId ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Users;
